/**
 * Ari Business deep links, shared by apps/ari and apps/ari-cloud.
 *
 * The base URL stays a parameter: each app reads its own `VITE_ARI_BUSINESS_URL`
 * through its config module, the same way `fetchMyWorkspaces` takes the
 * subscription-manager URL. Apps wrap these in a local `ari-business-url.ts`.
 */

/**
 * Query param that pins an Ari Business link to a workspace. Same name the
 * bc-idp login already uses (`startLogin(connection, returnTo, workspace)`).
 */
const WORKSPACE_PARAM = 'workspace';

/** Ari Business portal root. The CDN 301s `/` here and drops any query string, so
 *  every link that must survive the redirect (not just the billing link) has to
 *  target `/app` directly instead of the bare origin. */
export const ARI_BUSINESS_APP_PATH = '/app';

/** Business Cloud's billing page. Colossus CLOUD-294 adds this route.
 *  The old `/app/licenses` bounced to the dashboard behind the `ssplicensing` flag. */
export const ARI_BUSINESS_BILLING_PATH = `${ARI_BUSINESS_APP_PATH}/workspace/billing`;

/**
 * Build a link into Ari Business that carries the workspace the user is on in
 * Ari Cloud. Without it, someone signed into workspace A here and workspace B
 * over there lands on workspace B's pages (CLOUD-684).
 *
 * Falls back to the bare portal URL when no workspace is resolvable — an
 * unscoped link is still better than a dead button.
 */
export function buildAriBusinessUrl(baseUrl: string, workspaceId?: string | null, path?: string): string {
  const base = baseUrl.replace(/\/+$/, '');
  const suffix = path ? `/${path.replace(/^\/+/, '')}` : '';
  const url = `${base}${suffix}`;

  if (!workspaceId) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${WORKSPACE_PARAM}=${encodeURIComponent(workspaceId)}`;
}
